import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import "./index.scss";
import { Chip, CircularProgress, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import ConfirmModal from "../../../common-utilities/core/confirm-modal";
import { exportToExcel } from "../../../common-utilities/utils";

import { CfV2FormsTable } from "./cf-v2-forms-table";
import CfV2FilterModal from "./components/filter-modal";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME, INTRANETHOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { REST_URLS as INTRANET_URLS } from "../../../common-utilities/utils/end-points/intranet";
import { setFilters, removeFilter as removeFilterAction } from "../../../../reducer/cfApprovalV2";
import { DIRECT_DISPLAY_STATUSES } from "./v2-config";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

export const CFV2FormsStatusTabs = ({ status = null, count = 0, refreshKey }) => {
  const dispatch = useDispatch();
  const { filters: appliedFilters } = useSelector((state) => state.cfApprovalV2);
  const stepMaster = useSelector((state) => state.cfApprovalV2.stepMaster) || [];
  const stepNameMap = stepMaster.reduce((acc, s) => {
    acc[s.stepNumber] = s.name;
    return acc;
  }, {});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [userMap, setUserMap] = useState({});

  const loadUsers = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`,
      null,
      { appLink: "cf-approval-v2", page: 1, limit: 100 },
    ).then((res) => {
      if (Array.isArray(res?.results)) {
        const map = {};
        res.results.forEach((user) => {
          map[user.id] = user.name;
        });
        setUserMap(map);
      }
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleApplyFilters = (filters) => {
    dispatch(setFilters(filters));
    setShowFilterModal(false);
  };

  const removeFilter = (key) => {
    dispatch(removeFilterAction(key));
  };

  const handleDownload = () => {
    setDownloading(true);
    const queryParams = { page: 1, limit: count || 1, ...appliedFilters };
    if (status) queryParams.status = status;

    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}`, null, queryParams)
      .then((response) => {
        const rows = (response?.result || []).map((item) => ({
          "CF ID": item?.id || "",
          "Site Name": item?.siteName || "",
          "Region": item?.address?.region || "N/A",
          "Circle": item?.address?.circle || "N/A",
          "City": item?.address?.city || "N/A",
          "Relocated": item?.relocated ? "Yes" : "No",
          "Part of AOP List": item?.partOfAOP ? "Yes" : "No",
          "Current Step": item?.currentStepDetails
            ? `Step ${item.currentStepDetails.stepNumber} - ${item.currentStepDetails.name}`
            : "N/A",
          "Status": DIRECT_DISPLAY_STATUSES.includes(item?.status)
            ? item?.status
            : item?.currentStepDetails?.cfStatus,
          "Store Code": `${item.storeCode || 'x'}-${item.sequenceCode || 'x'}-${item.satelliteCode || 'x'}`,
          "Created By": userMap[item?.createdBy] || "N/A",
          "Created Date": item?.createdAt
            ? new Date(item.createdAt).toLocaleString("en-IN")
            : "",
        }));
        exportToExcel(rows, "CF_V2_Forms");
      })
      .catch(() => toast.error("Failed to download"))
      .finally(() => setDownloading(false));
  };

  const filterChips = [];
  if (appliedFilters.siteName) {
    filterChips.push({ key: "siteName", label: `Site: ${appliedFilters.siteName}` });
  }
  if (appliedFilters.id) {
    filterChips.push({ key: "id", label: `ID: ${appliedFilters.id}` });
  }
  if (appliedFilters.dateFrom || appliedFilters.dateTo) {
    filterChips.push({
      key: "date",
      label: `${formatDate(appliedFilters.dateFrom)} – ${formatDate(appliedFilters.dateTo)}`,
    });
  }
  if (appliedFilters.createdBy) {
    filterChips.push({ key: "createdBy", label: `Created By: ${userMap[appliedFilters.createdBy]}` });
  }
  if (appliedFilters.region) {
    filterChips.push({ key: "region", label: `Region: ${appliedFilters.region}` });
  }
  if (appliedFilters.circle) {
    filterChips.push({ key: "circle", label: `Circle: ${appliedFilters.circle}` });
  }
  if (appliedFilters.currentStep) {
    const stepName = stepNameMap[appliedFilters.currentStep];
    filterChips.push({
      key: "currentStep",
      label: `Step ${appliedFilters.currentStep}${stepName ? ` - ${stepName}` : ""}`,
    });
  }
  if (appliedFilters.cfStatus) {
    filterChips.push({ key: "cfStatus", label: `Status: ${appliedFilters.cfStatus}` });
  }

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Grid container spacing={0} alignItems="center" justifyContent="flex-end">
          <Grid item md={12} xs={12}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              sx={{ pr: 1, py: 0.5 }}
            >
              {filterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  onDelete={() => removeFilter(chip.key)}
                  sx={{ fontSize: "0.7rem" }}
                />
              ))}
              <Tooltip title="Download">
                <span>
                  <IconButton
                    onClick={handleDownload}
                    size="small"
                    disabled={downloading}
                  >
                    {downloading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Filters">
                <IconButton
                  onClick={() => setShowFilterModal(true)}
                  size="small"
                  color={filterChips.length > 0 ? "primary" : "default"}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <CfV2FormsTable
        commonFilters={appliedFilters}
        status={status}
        refreshKey={refreshKey}
        userMap={userMap}
      />
      {showFilterModal && (
        <CfV2FilterModal
          onClose={() => setShowFilterModal(false)}
          onApply={handleApplyFilters}
          initialFilters={appliedFilters}
          userMap={userMap}
        />
      )}
    </div>
  );
};
